import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Smartphone, Share, Download, X, Plus } from 'lucide-react';

const DISMISS_KEY = 'staff_install_dismissed_at';
const REPROMPT_DAYS = 14;

/**
 * Compact, dismissible install card for the staff PWA. Three behaviours:
 *  - Already installed (display-mode: standalone) → renders nothing.
 *  - Android / Chrome desktop with a captured `beforeinstallprompt` event →
 *    shows an "Install" button that triggers the native prompt.
 *  - iOS Safari (no install API) → shows step-by-step "Add to Home Screen"
 *    instructions because Apple still doesn't allow programmatic installs.
 *
 * Re-prompts at most once every 14 days after a dismiss.
 */
export function InstallStaffPwaCard() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosTips, setShowIosTips] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return true;
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts > 0 && Date.now() - ts < REPROMPT_DAYS * 86400_000;
  });

  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
  const isIos = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent || '');

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  if (isStandalone || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === 'accepted') setDismissed(true);
  };

  if (showIosTips) {
    const isDesktop = !isIos && typeof navigator !== 'undefined' && !/Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
    return (
      <Card className="bg-emerald-50 border-emerald-200" data-testid="install-ios-tips">
        <CardContent className="p-4 relative">
          <button onClick={() => setShowIosTips(false)} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" data-testid="install-tips-close">
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-700 text-white flex items-center justify-center flex-shrink-0">
              <Smartphone className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0 pr-6">
              {isDesktop ? (
                <>
                  <p className="font-semibold text-sm">Install on your phone</p>
                  <p className="text-xs text-emerald-900/80 mt-2">Open this page on your phone's browser:</p>
                  <p className="text-xs font-mono bg-white/60 rounded px-2 py-1 mt-1.5 break-all">{typeof window !== 'undefined' ? window.location.host : ''}/staff/home</p>
                  <p className="text-xs text-emerald-900/80 mt-3">Then:</p>
                  <ul className="text-xs text-emerald-900/80 mt-1 space-y-1.5 list-disc pl-4">
                    <li><span className="font-medium">iPhone:</span> tap <Share className="inline h-3 w-3 mx-0.5 align-text-bottom" /> Share → <span className="font-medium">Add to Home Screen</span></li>
                    <li><span className="font-medium">Android:</span> Chrome menu (⋮) → <span className="font-medium">Install app</span></li>
                  </ul>
                </>
              ) : (
                <>
                  <p className="font-semibold text-sm">Install on iPhone</p>
                  <ol className="text-xs text-emerald-900/80 mt-2 space-y-1.5 list-decimal pl-4">
                    <li>Tap the <Share className="inline h-3 w-3 mx-0.5 align-text-bottom" /> Share button at the bottom of Safari.</li>
                    <li>Scroll down and pick <span className="font-medium">Add to Home Screen</span>.</li>
                    <li>Tap <span className="font-medium">Add</span> in the top-right corner.</li>
                  </ol>
                  <p className="text-[11px] text-emerald-800/70 mt-2">An <span className="font-mono">FL Staff</span> icon will appear on your home screen — just like a regular app.</p>
                </>
              )}
              <Button size="sm" variant="ghost" className="mt-2 -ml-2 h-7 text-xs text-emerald-800" onClick={dismiss} data-testid="install-tips-dismiss">Don't show again</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-emerald-50 border-emerald-200" data-testid="install-card">
      <CardContent className="p-4 relative">
        <button onClick={dismiss} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground" data-testid="install-card-dismiss">
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="h-10 w-10 rounded-xl bg-emerald-700 text-white flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Install Flex Living Staff</p>
            <p className="text-xs text-emerald-900/70 mt-0.5">One tap to log hours from anywhere — no browser, no bookmarks.</p>
            <div className="mt-3 flex gap-2">
              {deferredPrompt ? (
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={triggerInstall} data-testid="install-card-prompt">
                  <Download className="h-3.5 w-3.5 mr-1.5" />Install app
                </Button>
              ) : (
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setShowIosTips(true)} data-testid="install-card-show-ios">
                  <Plus className="h-3.5 w-3.5 mr-1.5" />Add to home screen
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
